<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCompanyLabelsStore } from 'dashboard/stores/companyLabels';

import BulkSelectBar from 'dashboard/components-next/captain/assistant/BulkSelectBar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Policy from 'dashboard/components/policy.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  visibleCompanyIds: {
    type: Array,
    default: () => [],
  },
  selectedCompanyIds: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'cancelSelection',
  'assignLabels',
  'removeLabels',
  'toggleAll',
  'exportSelected',
  'deleteSelected',
]);

const { t } = useI18n();
const labelsStore = useCompanyLabelsStore();

const SENTINEL_ID = -1;

const selectedCount = computed(() => props.selectedCompanyIds.length);
const totalVisibleCompanies = computed(() => props.visibleCompanyIds.length);

const selectAllLabel = computed(() => {
  if (!totalVisibleCompanies.value) return '';
  return t('COMPANIES_BULK_ACTIONS.SELECT_ALL', {
    count: totalVisibleCompanies.value,
  });
});

const selectedCountLabel = computed(() =>
  t('COMPANIES_BULK_ACTIONS.SELECTED_COUNT', {
    count: selectedCount.value,
  })
);

const allItems = computed(() => props.visibleCompanyIds.map(id => ({ id })));

const selectionModel = computed({
  get: () => {
    const set = new Set(props.selectedCompanyIds);
    set.add(SENTINEL_ID);
    return set;
  },
  set: newSet => {
    if (!props.visibleCompanyIds.length) {
      emit('toggleAll', false);
      return;
    }
    const shouldSelectAll = props.visibleCompanyIds.every(id => newSet.has(id));
    emit('toggleAll', shouldSelectAll);
  },
});

// --- Assign labels dropdown ---
const showAssignDropdown = ref(false);
const showRemoveDropdown = ref(false);
const selectedAssignLabels = ref([]);
const selectedRemoveLabels = ref([]);
const assignSearchQuery = ref('');
const removeSearchQuery = ref('');
const isCreatingAssign = ref(false);

const allLabels = computed(() => labelsStore.getLabels);

const canCreateAssignLabel = computed(() => {
  const q = assignSearchQuery.value.trim().toLowerCase();
  if (!q) return false;
  return !allLabels.value.some(l => l.title.toLowerCase() === q);
});

const assignLabelItems = computed(() =>
  allLabels.value.map(l => ({
    action: 'select',
    value: l.title,
    label: l.title,
    color: l.color,
    id: l.id,
    isSelected: selectedAssignLabels.value.includes(l.title),
  }))
);

const removeLabelItems = computed(() =>
  allLabels.value.map(l => ({
    action: 'select',
    value: l.title,
    label: l.title,
    color: l.color,
    id: l.id,
    isSelected: selectedRemoveLabels.value.includes(l.title),
  }))
);

const toggleAssignLabel = title => {
  const idx = selectedAssignLabels.value.indexOf(title);
  if (idx > -1) selectedAssignLabels.value.splice(idx, 1);
  else selectedAssignLabels.value.push(title);
};

const toggleRemoveLabel = title => {
  const idx = selectedRemoveLabels.value.indexOf(title);
  if (idx > -1) selectedRemoveLabels.value.splice(idx, 1);
  else selectedRemoveLabels.value.push(title);
};

const handleCreateAndAssignLabel = async () => {
  const title = assignSearchQuery.value.trim();
  if (!title || isCreatingAssign.value) return;
  isCreatingAssign.value = true;
  try {
    await labelsStore.create({
      title,
      color: '#6B7280',
    });
    toggleAssignLabel(title);
    assignSearchQuery.value = '';
  } finally {
    isCreatingAssign.value = false;
  }
};

const handleAssignEnter = () => {
  if (canCreateAssignLabel.value) handleCreateAndAssignLabel();
};

const applyAssignLabels = () => {
  if (!selectedAssignLabels.value.length) return;
  emit('assignLabels', [...selectedAssignLabels.value]);
  selectedAssignLabels.value = [];
  assignSearchQuery.value = '';
  showAssignDropdown.value = false;
};

const applyRemoveLabels = () => {
  if (!selectedRemoveLabels.value.length) return;
  emit('removeLabels', [...selectedRemoveLabels.value]);
  selectedRemoveLabels.value = [];
  removeSearchQuery.value = '';
  showRemoveDropdown.value = false;
};
</script>

<template>
  <div
    class="sticky top-0 z-10 bg-gradient-to-b from-n-surface-1 from-90% to-transparent pt-1 pb-2"
  >
    <BulkSelectBar
      v-model="selectionModel"
      :all-items="allItems"
      :select-all-label="selectAllLabel"
      :selected-count-label="selectedCountLabel"
      class="py-2 ltr:!pr-3 rtl:!pl-3 justify-between"
    >
      <template #primaryActions>
        <Button
          sm
          ghost
          ruby
          icon="i-lucide-x"
          :label="t('COMPANIES_BULK_ACTIONS.CANCEL_SELECTION')"
          class="!px-1"
          @click="emit('cancelSelection')"
        />
      </template>
      <template #actions>
        <div class="flex items-center gap-2 ml-auto">
          <!-- Assign labels -->
          <div class="relative">
            <Button
              v-tooltip.bottom="t('COMPANIES_BULK_ACTIONS.ASSIGN_LABELS')"
              sm
              ghost
              slate
              icon="i-lucide-tag"
              :label="t('COMPANIES_BULK_ACTIONS.ASSIGN_LABELS')"
              :disabled="!selectedCount || isLoading"
              :is-loading="isLoading"
              class="!px-2 [&>span:nth-child(2)]:hidden md:[&>span:nth-child(2)]:inline-flex w-fit !text-n-blue-11 [&>span]:!text-n-blue-11"
              @click="
                showAssignDropdown = !showAssignDropdown;
                showRemoveDropdown = false;
              "
            />
            <DropdownMenu
              v-if="showAssignDropdown"
              v-on-clickaway="() => (showAssignDropdown = false)"
              :menu-items="assignLabelItems"
              show-search
              :search-placeholder="t('BULK_ACTION.SEARCH_INPUT_PLACEHOLDER')"
              class="ltr:right-0 rtl:left-0 mt-1 w-64 max-h-80 top-full"
              @action="item => toggleAssignLabel(item.value)"
              @search="v => (assignSearchQuery = v)"
              @keydown.enter.capture="handleAssignEnter"
            >
              <template #thumbnail="{ item }">
                <span
                  class="rounded-md h-3 w-3 flex-shrink-0 border border-solid border-n-weak"
                  :style="{ backgroundColor: item.color }"
                />
              </template>
              <template #trailing-icon="{ item }">
                <Icon
                  v-if="item.isSelected"
                  icon="i-lucide-check"
                  class="size-4 text-n-blue-11 flex-shrink-0"
                />
              </template>
              <template #footer>
                <div
                  class="sticky bottom-0 rounded-b-md px-2 py-2 z-20 bg-n-alpha-3 backdrop-blur-[4px] flex flex-col gap-1.5"
                >
                  <button
                    v-if="canCreateAssignLabel"
                    type="button"
                    class="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-lg text-n-slate-12 hover:bg-n-alpha-1 transition-colors text-left"
                    :disabled="isCreatingAssign"
                    @click="handleCreateAndAssignLabel"
                  >
                    <Icon
                      icon="i-lucide-corner-down-left"
                      class="size-3.5 shrink-0 text-n-slate-11"
                    />
                    <span>{{
                      t('BULK_ACTION.LABELS.CREATE_LABEL', {
                        title: assignSearchQuery.trim(),
                      })
                    }}</span>
                  </button>
                  <Button
                    sm
                    class="w-full [&>span:nth-child(2)]:hidden md:[&>span:nth-child(2)]:inline-flex"
                    :label="t('BULK_ACTION.LABELS.ASSIGN_SELECTED_LABELS')"
                    :disabled="!selectedAssignLabels.length"
                    @click="applyAssignLabels"
                  />
                </div>
              </template>
            </DropdownMenu>
          </div>

          <!-- Remove labels -->
          <div class="relative">
            <Button
              v-tooltip.bottom="t('COMPANIES_BULK_ACTIONS.REMOVE_LABELS')"
              sm
              ghost
              slate
              icon="i-woot-tag-remove"
              :label="t('COMPANIES_BULK_ACTIONS.REMOVE_LABELS')"
              :disabled="!selectedCount || isLoading"
              :is-loading="isLoading"
              class="!px-2 [&>span:nth-child(2)]:hidden md:[&>span:nth-child(2)]:inline-flex w-fit !text-n-blue-11 [&>span]:!text-n-blue-11"
              @click="
                showRemoveDropdown = !showRemoveDropdown;
                showAssignDropdown = false;
              "
            />
            <DropdownMenu
              v-if="showRemoveDropdown"
              v-on-clickaway="() => (showRemoveDropdown = false)"
              :menu-items="removeLabelItems"
              show-search
              :search-placeholder="t('BULK_ACTION.SEARCH_INPUT_PLACEHOLDER')"
              class="ltr:right-0 rtl:left-0 mt-1 w-64 max-h-80 top-full"
              @action="item => toggleRemoveLabel(item.value)"
              @search="v => (removeSearchQuery = v)"
            >
              <template #thumbnail="{ item }">
                <span
                  class="rounded-md h-3 w-3 flex-shrink-0 border border-solid border-n-weak"
                  :style="{ backgroundColor: item.color }"
                />
              </template>
              <template #trailing-icon="{ item }">
                <Icon
                  v-if="item.isSelected"
                  icon="i-lucide-check"
                  class="size-4 text-n-blue-11 flex-shrink-0"
                />
              </template>
              <template #footer>
                <div
                  class="sticky bottom-0 rounded-b-md px-2 py-2 z-20 bg-n-alpha-3 backdrop-blur-[4px]"
                >
                  <Button
                    sm
                    class="w-full [&>span:nth-child(2)]:hidden md:[&>span:nth-child(2)]:inline-flex"
                    :label="t('BULK_ACTION.LABELS.REMOVE_SELECTED_LABELS')"
                    :disabled="!selectedRemoveLabels.length"
                    @click="applyRemoveLabels"
                  />
                </div>
              </template>
            </DropdownMenu>
          </div>

          <Button
            v-tooltip.bottom="t('COMPANIES_BULK_ACTIONS.EXPORT_SELECTED')"
            sm
            ghost
            slate
            icon="i-lucide-upload"
            :label="t('COMPANIES_BULK_ACTIONS.EXPORT_SELECTED')"
            :aria-label="t('COMPANIES_BULK_ACTIONS.EXPORT_SELECTED')"
            :disabled="isLoading"
            class="!px-2 [&>span:nth-child(2)]:hidden md:[&>span:nth-child(2)]:inline-flex"
            @click="emit('exportSelected')"
          />
          <div class="w-px h-3 bg-n-weak rounded-lg" />
          <Policy :permissions="['administrator']">
            <Button
              v-tooltip.bottom="t('COMPANIES_BULK_ACTIONS.DELETE_COMPANIES')"
              sm
              ghost
              ruby
              icon="i-lucide-trash"
              :label="t('COMPANIES_BULK_ACTIONS.DELETE_COMPANIES')"
              :aria-label="t('COMPANIES_BULK_ACTIONS.DELETE_COMPANIES')"
              :disabled="!selectedCount || isLoading"
              :is-loading="isLoading"
              class="!px-2 [&>span:nth-child(2)]:hidden md:[&>span:nth-child(2)]:inline-flex"
              @click="emit('deleteSelected')"
            />
          </Policy>
        </div>
      </template>
    </BulkSelectBar>
  </div>
</template>
